import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, FreeMode, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { X, Image as ImageIcon } from "lucide-react";
import { OptimizedImage } from "@/components/common/OptimizedImage";

const RoomImageSlider = ({ data }: { data: any[] }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialSlideIndex, setInitialSlideIndex] = useState(0);
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);

  const openImageModal = (index: number) => {
    setInitialSlideIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setThumbsSwiper(null);
  };

  return (
    <div className="signatureMomentSwiper">
      {data && data?.length > 0 ? (
        <>
          <Swiper
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            loop={true}
            spaceBetween={10}
            slidesPerView={1}
            navigation={true}
            modules={[
              // Autoplay,
              Navigation,
            ]}
            style={{ padding: "0 0 30px 0" }}
          >
            {data?.map((room: any, index: number) => (
              <SwiperSlide key={index}>
                <div
                  className="cursor-pointer group relative overflow-hidden"
                  onClick={() => openImageModal(index)}
                  style={{
                    height: 385,
                    width: "100%",
                  }}
                >
                  <Image
                    src={room?.ImageGallery}
                    alt={`Room Image ${index + 1}`}
                    className="w-full h-full group-hover:scale-110 object-cover transition-transform duration-700"
                    height={385}
                    width={616}
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Fullscreen Swiper Modal */}
          <AnimatePresence>
            {isModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex flex-col"
              >
                {/* Modal Header */}
                <div className="flex justify-between items-center p-6 text-white absolute top-0 left-0 right-0 z-10">
                  <div className="max-w-[70%]">
                    <h3 className="text-2xl font-light">Gallery View</h3>
                  </div>
                  <button
                    onClick={closeModal}
                    className="bg-white/10 hover:bg-white/20 p-3 rounded-full backdrop-blur-md transition-all"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Swiper Container */}
                <div className="flex-1 flex flex-col justify-center items-center p-4 md:p-6 mt-16 w-full max-w-7xl mx-auto overflow-hidden">
                  {/* Main Slider */}
                  <div className="w-full h-[50vh] md:h-[70vh] mb-4">
                    <Swiper
                      style={
                        {
                          "--swiper-navigation-color": "#fff",
                          "--swiper-pagination-color": "#fff",
                        } as any
                      }
                      loop={true}
                      spaceBetween={10}
                      navigation={true}
                      thumbs={{
                        swiper:
                          thumbsSwiper && !thumbsSwiper.destroyed
                            ? thumbsSwiper
                            : null,
                      }}
                      modules={[FreeMode, Navigation, Thumbs]}
                      initialSlide={initialSlideIndex}
                      className="w-full h-full rounded-2xl overflow-hidden"
                    >
                      {data?.map((room: any, idx: number) => (
                        <SwiperSlide
                          key={`main-${idx}`}
                          className="flex justify-center items-center bg-transparent relative w-full h-full"
                        >
                          <OptimizedImage
                            src={room?.ImageGallery}
                            alt={`Gallery image ${idx + 1}`}
                            sizes="100vw"
                            className="w-full h-[50vh] md:h-[70vh]"
                            aspectRatio="auto"
                            objectFit="contain"
                            style={{ backgroundColor: "transparent" }}
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>

                  {/* Thumbnail Slider */}
                  <div className="w-full mt-auto">
                    <Swiper
                      onSwiper={setThumbsSwiper}
                      spaceBetween={10}
                      slidesPerView={3}
                      freeMode={true}
                      centeredSlides={true}
                      watchSlidesProgress={true}
                      modules={[FreeMode, Navigation, Thumbs]}
                      initialSlide={initialSlideIndex}
                      breakpoints={{
                        360: { slidesPerView: 4 },
                        640: { slidesPerView: 5 },
                        768: { slidesPerView: 7 },
                        1024: { slidesPerView: 10 },
                      }}
                      className="w-full h-full thumbs-swiper"
                    >
                      {data?.map((room: any, idx: number) => (
                        <SwiperSlide
                          key={`thumb-${idx}`}
                          className="cursor-pointer opacity-50 transition-opacity [&.swiper-slide-thumb-active]:opacity-100 rounded-lg overflow-hidden border-2 border-transparent [&.swiper-slide-thumb-active]:border-[#C9A962]"
                        >
                          <OptimizedImage
                            src={room?.ImageGallery}
                            alt={`Thumbnail ${idx}`}
                            sizes="(max-width: 768px) 25vw, 15vw"
                            className="w-full h-full"
                            aspectRatio="1/1"
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : null}
    </div>
  );
};

export default RoomImageSlider;
